import React, { ReactNode } from "react";
import ReportHeader from "../../reports/components/ReportHeader";
import { PrimaryButton } from "../../../components/Button";
import NewReportFilters, { ReportFilterValue } from "./NewReportFilters";
import { WindowInfo } from "../api/types";

// Any response the shell can render: it needs the echoed window; `rows` is optional and only used
// for the empty-state check (rollup/detail responses have it; the reconciliation response doesn't).
interface ShellData {
  window: WindowInfo;
  rows?: unknown[];
}

interface ReportPageShellProps<T extends ShellData> {
  title: string;
  tooltipText?: string;
  filters: ReportFilterValue;
  onFiltersChange: (value: ReportFilterValue) => void;
  state: { data: T | null; loading: boolean; error: string | null };
  // Rendered only once data is loaded. Receives the full response.
  children: (data: T) => ReactNode;
  // Optional extra controls rendered on the filter row (e.g. metric toggles, export).
  actions?: ReactNode;
  // Shown when there's no data yet and we're not loading/erroring (e.g. "select an event").
  idlePrompt?: ReactNode;
  // Add vendor / location dropdowns to the filter bar.
  filterVendor?: boolean;
  filterLocation?: boolean;
  // Explicit-search wiring: the page only fetches when the user clicks Search. `onSearch` submits
  // the current filters/controls; `isDirty` = there are unsubmitted changes; `hasRun` = at least
  // one Search has happened. When these are omitted the Search bar is hidden (legacy pages that
  // still fetch on change).
  onSearch?: () => void;
  isDirty?: boolean;
  hasRun?: boolean;
}

// Shared chrome for every NEW report page: mirrors the old reports' look (white card,
// ReportHeader, filter row) and adds a clear "new engine" banner plus the window/day-boundary
// line the engine echoes back, so QA always knows which surface they're looking at.
export default function ReportPageShell<T extends ShellData>({
  title,
  tooltipText,
  filters,
  onFiltersChange,
  state,
  children,
  actions,
  idlePrompt,
  filterVendor,
  filterLocation,
  onSearch,
  isDirty,
  hasRun,
}: ReportPageShellProps<T>) {
  const { data, loading, error } = state;
  const searchable = !!onSearch;
  // Before the first Search the report is idle by design — nothing has been requested yet.
  const awaitingFirstSearch = searchable && !hasRun;

  return (
    <>
      <div className="mb-3 rounded-md bg-white px-4 pb-2">
        <div className="flex items-center justify-between pt-2">
          <span className="inline-flex items-center rounded-full bg-primary-600 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
            New Reporting Engine
          </span>
          {data && (
            <span className="text-xs text-gray-500">
              {data.window.from} → {data.window.to} · {data.window.dayBoundary}
            </span>
          )}
        </div>

        <ReportHeader
          title={title}
          toolTipShow={!!tooltipText}
          tooltipText={tooltipText}
        />

        <NewReportFilters
          value={filters}
          onChange={onFiltersChange}
          includeVendor={filterVendor}
          includeLocation={filterLocation}
        />

        {actions && <div className="flex flex-wrap items-center gap-2 pb-2">{actions}</div>}

        {searchable && (
          <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3 pb-1">
            <PrimaryButton
              onClick={onSearch}
              isLoading={loading}
              isDisabled={loading || (hasRun && !isDirty)}
            >
              Search
            </PrimaryButton>
            {!loading && awaitingFirstSearch && (
              <span className="text-xs text-gray-500">
                Set the event, group-by, dates, and any other filters, then click Search to run the
                report.
              </span>
            )}
            {!loading && hasRun && isDirty && (
              <span className="text-xs font-medium text-amber-700">
                Filters changed — click Search to update the results below.
              </span>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div className="font-semibold">Could not load this report from the new engine.</div>
          <div className="mt-1 break-words">{error}</div>
          <div className="mt-2 text-xs text-red-600">
            Check that <code>Ronin.ApiService</code> is running (default
            <code> http://localhost:5382</code>) and reachable through the Vite proxy.
          </div>
        </div>
      )}

      {loading && !data && (
        <div className="rounded-md bg-white px-4 py-10 text-center text-sm text-gray-500">
          Loading from the new reporting engine…
        </div>
      )}

      {awaitingFirstSearch && !loading && !error && (
        <div className="rounded-md bg-white px-4 py-10 text-center text-sm text-gray-500">
          Choose your filters above and click <span className="font-semibold">Search</span> to run
          this report.
        </div>
      )}

      {!awaitingFirstSearch && !loading && !data && !error && idlePrompt && (
        <div className="rounded-md bg-white px-4 py-10 text-center text-sm text-gray-500">
          {idlePrompt}
        </div>
      )}

      {data && !error && (
        <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
          {data.rows && data.rows.length === 0 ? (
            <div className="rounded-md bg-white px-4 py-10 text-center text-sm text-gray-500">
              No data for the selected filters.
            </div>
          ) : (
            children(data)
          )}
        </div>
      )}
    </>
  );
}
